import { SetBookOfMindState } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Helpers/BookOfMindHelpers";
import { GetSpeechItems } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Tools/Speak/GetSpeechItems";
import { ReadState } from "source/app/services/UI/State/ReadState";
import { IBookOfMindState } from "../Helpers/IBookOfMindState";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { WaitAsync } from "./Helpers/WaitAsync";
import { fromOthersNodeName } from "motw_core/Repositories/WorkflowRepository";
import NoSleep from "nosleep.js";
import Ambience from "source/app/services/Sound/Ambience/Ambience";
import Music from "source/app/services/Sound/Music/Music";
import { Sounds } from "source/app/services/Sound/Sounds";
import {
    GetClientErrorDetails,
    LogClientDiagnostic,
} from "source/app/services/ClientDiagnostics";
import {
    GetGoogleBookOfMindSpeechSettings,
    GetGoogleBookOfMindVoiceNameForQuote,
    GetBookOfMindSavedVoiceName,
    IsGoogleBookOfMindAlternatingVoice,
    IsGoogleBookOfMindVoice,
    ResolveBookOfMindVoice,
} from "./Helpers/BookOfMindVoice";
import MotwLogicServer, {
    IBookOfMindPageSpeechJob,
} from "source/app/services/External/LogicServer/MotwLogicServer";
import { IBookOfMindPageAudioDiagnostics } from "source/app/services/External/LogicServer/IBookOfMindPageAudioDiagnostics";
import { GetAudioDiagnostics } from "./Helpers/GetAudioDiagnostics";
import { googleAudioUnlockSource } from "./Helpers/GoogleAudioPlayback";

const pageAudioJobPollIntervalMs = 2000;
const pageAudioJobMaximumWaitMs = 15 * 60 * 1000;
const googleAudioUnlockMaximumWaitMs = 8000;

// To fix an iOS garbage-collection bug
let windowUtterances: SpeechSynthesisUtterance[] = [];

export type BookOfMindPageAudioState =
    | "idle"
    | "preparing"
    | "playing"
    | "paused"
    | "ready";

export interface IBookOfMindPageAudioSnapshot {
    state: BookOfMindPageAudioState;
    completedSteps?: number;
    totalSteps?: number;
    error?: string;
}

interface IPrefetchedGoogleSpeech {
    sequenceId: number;
    text: string;
    voiceName: string;
    audioPromise: Promise<Blob>;
}

interface IPageAudioJobRequest {
    clientRequestId: string;
    title: string;
    quotes: string[];
    includeTitle: boolean;
    voiceName: string;
    speechSettings: {
        speakingRate: number;
        pitch: number;
    };
    pageAudioDiagnostics?: IBookOfMindPageAudioDiagnostics;
    quoteSpeechSettings?: Array<{
        voiceName: string;
        speakingRate: number;
        pitch: number;
    }>;
}

class BookOfMindSpeech {
    private activeWordHighlight?: IActiveWordHighlight;
    private pendingStartTimeoutId?: number;
    private pauseRetryTimeoutId?: number;
    private pauseRequestId = 0;
    private preservePendingStartOnNextStop = false;
    private googleSpeechRequestId = 0;
    private activeGoogleSpeechRequestId?: number;
    private activeGoogleAudio?: HTMLAudioElement;
    private activeGoogleAudioUrl?: string;
    private finishActiveGoogleAudio?: () => void;
    private isGoogleSpeechPaused = false;
    private googleAudioElement?: HTMLAudioElement;
    private googleAudioUnlockPromise?: Promise<void>;
    private googleSpeechSequenceId = 0;
    private prefetchedGoogleSpeech?: IPrefetchedGoogleSpeech;
    private pageAudioRequestId = 0;
    private pageAudioUrl?: string;
    private pageAudioBlob?: Blob;
    private pageAudioKey?: string;
    private pageAudioTitle?: string;
    private pageAudioJobId?: string;
    private pageAudioJobRequest?: IPageAudioJobRequest;
    private pageAudioClientRequestId?: string;
    private pageAudioResumePromise?: Promise<void>;
    private pageAudioState: BookOfMindPageAudioState = "idle";
    private pageAudioError?: string;
    private pageAudioCompletedSteps?: number;
    private pageAudioTotalSteps?: number;
    private pageAudioProgressCueTimerId?: number;
    private pageAudioProgressCueQueue: number[] = [];
    private pageAudioProgressCuePendingAfterResume = false;
    private pageAudioProgressCueLastStep = 0;
    private pageAudioCompletionCueTimeoutId?: number;
    private pageAudioCompletionCuePending = false;
    private pageAudioCompletionCuePlayed = false;
    private pageAudioListeners = new Set<
        (snapshot: IBookOfMindPageAudioSnapshot) => void
    >();

    constructor() {
        if (typeof document === "undefined") {
            return;
        }

        document.addEventListener(
            "visibilitychange",
            this.HandlePageAudioResume
        );
        window.addEventListener("pageshow", this.HandlePageAudioResume);
    }

    async StartAsync() {
        LogClientDiagnostic("book-of-mind-speech", "start-request");
        this.StopPageAudio("speech-start");
        this.ClearPendingStart();
        this.preservePendingStartOnNextStop = false;
        this.InvalidateGoogleSpeechPrefetch();
        SetBookOfMindState({ isSpeechStarting: true });
        if (IsGoogleBookOfMindVoice(GetBookOfMindSavedVoiceName())) {
            // Start the reusable element while StartAsync is still inside the
            // user's activation before the first Google request can await.
            this.PrepareGoogleAudioForUserGesture();
        }
        try {
            const initialItemsToSpeak = GetSpeechItems();
            const firstQuote = initialItemsToSpeak[0];
            if (firstQuote) {
                // Start the first quote request while the root title is being
                // spoken, so B2/Google latency is hidden behind narration.
                this.PrefetchNextGoogleSpeech(firstQuote, 0);
            }

            const rootNodeNameWasAnnounced = await this.PlayRootNodeName();
            if (rootNodeNameWasAnnounced) {
                await WaitAsync(1250);
            }

            const itemsToSpeak = GetSpeechItems();

            SetBookOfMindState({
                itemsToSpeak,
                isSpeechStarting: false,
                isSpeechActive: true,
            });
            LogClientDiagnostic("book-of-mind-speech", "start-complete");
        } catch (error) {
            LogClientDiagnostic(
                "book-of-mind-speech",
                "start-error",
                { error: GetClientErrorDetails(error) },
                "error"
            );
            console.error("Book of Mind speech failed to start", error);
            this.StopSpeech();
        }
    }

    SpeakItemInChainAsync(
        text: string,
        quoteIndex?: number,
        onPlaybackStarted?: () => void
    ) {
        return this.SpeakTextAsync(
            text,
            undefined,
            true,
            quoteIndex,
            onPlaybackStarted
        );
    }

    SpeakSingleTextAsync(text: string) {
        return this.SpeakTextAsync(text);
    }

    SpeakPreviewTextAsync(
        text: string,
        preferredVoiceName: string
    ): Promise<string> {
        const voices = window.speechSynthesis.getVoices();

        if (voices.length > 0) {
            return this.SpeakTextWithVoicesAsync(
                text,
                voices,
                preferredVoiceName
            );
        }

        return this.SpeakTextAsync(text, preferredVoiceName);
    }

    SpeakPreviewTextWithVoice(
        text: string,
        voice: SpeechSynthesisVoice
    ): Promise<string> {
        return this.SpeakTextWithVoiceAsync(text, voice);
    }

    GetPageAudioSnapshot(): IBookOfMindPageAudioSnapshot {
        return {
            state: this.pageAudioState,
            completedSteps: this.pageAudioCompletedSteps,
            totalSteps: this.pageAudioTotalSteps,
            error: this.pageAudioError,
        };
    }

    SubscribePageAudio(
        listener: (snapshot: IBookOfMindPageAudioSnapshot) => void
    ) {
        this.pageAudioListeners.add(listener);
        listener(this.GetPageAudioSnapshot());
        return () => this.pageAudioListeners.delete(listener);
    }

    async PlayPageAudioAsync(
        title: string,
        quotes: string[],
        includeTitle = true,
        pageAudioDiagnostics?: IBookOfMindPageAudioDiagnostics
    ) {
        const savedVoiceName = GetBookOfMindSavedVoiceName();
        const pageKey = `${savedVoiceName}\u0000${includeTitle}\u0000${title}\u0000${quotes.join(
            "\u0000"
        )}`;

        if (
            this.pageAudioKey === pageKey &&
            this.pageAudioBlob &&
            (this.pageAudioState === "paused" ||
                this.pageAudioState === "ready")
        ) {
            await this.ResumePageAudioAsync();
            return;
        }

        if (!IsGoogleBookOfMindVoice(savedVoiceName)) {
            throw new Error(
                "Page audio requires a Google WaveNet Book of Mind voice."
            );
        }

        this.StopSpeech();
        const requestId = ++this.pageAudioRequestId;
        const clientRequestId = CreatePageAudioClientRequestId();
        this.pageAudioKey = pageKey;
        this.pageAudioTitle = title;
        this.pageAudioClientRequestId = clientRequestId;
        this.pageAudioJobId = undefined;
        this.pageAudioBlob = undefined;
        this.pageAudioError = undefined;
        this.pageAudioCompletedSteps = 0;
        this.pageAudioTotalSteps = quotes.length + (includeTitle ? 2 : 1);
        this.ResetPageAudioCueState();
        this.SetPageAudioState("preparing");
        LogClientDiagnostic(
            "book-of-mind-speech",
            "page-audio-request-start",
            {
                clientRequestId,
                requestId,
                titleLength: title.length,
                quoteCount: quotes.length,
                textLength: title.length + quotes.join("").length,
                voiceName: savedVoiceName,
                includeTitle,
                ...pageAudioDiagnostics,
            }
        );
        this.PrepareGoogleAudioForUserGesture();
        this.PlayPageAudioStartCue();

        try {
            const pageAudioJobRequest = this.CreatePageAudioJobRequest(
                title,
                quotes,
                savedVoiceName,
                clientRequestId,
                includeTitle,
                pageAudioDiagnostics
            );
            this.pageAudioJobRequest = pageAudioJobRequest;
            await this.StartOrResumePageAudioJobAsync(
                requestId,
                pageAudioJobRequest
            );
        } catch (error) {
            if (requestId !== this.pageAudioRequestId) {
                return;
            }

            const errorDetails = GetClientErrorDetails(error);
            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-error",
                {
                    clientRequestId,
                    requestId,
                    error: errorDetails,
                },
                "error"
            );
            this.SetPageAudioError(
                typeof errorDetails === "string"
                    ? errorDetails
                    : JSON.stringify(errorDetails) ??
                          "Unable to prepare page audio"
            );
            throw error;
        }
    }

    private CreatePageAudioJobRequest(
        title: string,
        quotes: string[],
        savedVoiceName: string,
        clientRequestId: string,
        includeTitle: boolean,
        pageAudioDiagnostics?: IBookOfMindPageAudioDiagnostics
    ): IPageAudioJobRequest {
        const voiceName = GetGoogleBookOfMindVoiceNameForQuote(
            savedVoiceName
        );
        const quoteSpeechSettings =
            IsGoogleBookOfMindAlternatingVoice(savedVoiceName)
                ? quotes.map((_, quoteIndex) => {
                      const quoteVoiceName =
                          GetGoogleBookOfMindVoiceNameForQuote(
                              savedVoiceName,
                              quoteIndex
                          );
                      const speechSettings =
                          GetGoogleBookOfMindSpeechSettings(quoteVoiceName) ??
                          { speakingRate: 1, pitch: 0 };

                      return {
                          voiceName: quoteVoiceName,
                          ...speechSettings,
                      };
                  })
                : undefined;

        return {
            clientRequestId,
            title,
            quotes,
            includeTitle,
            voiceName,
            speechSettings: GetGoogleBookOfMindSpeechSettings(voiceName) ?? {
                speakingRate: 1,
                pitch: 0,
            },
            pageAudioDiagnostics,
            quoteSpeechSettings,
        };
    }

    private async StartOrResumePageAudioJobAsync(
        requestId: number,
        request: IPageAudioJobRequest
    ) {
        if (requestId !== this.pageAudioRequestId) {
            return;
        }

        let jobId = this.pageAudioJobId;
        if (!jobId) {
            const job =
                await MotwLogicServer.StartBookOfMindPageSpeechAsync(
                    request.title,
                    request.quotes,
                    request.voiceName,
                    request.speechSettings,
                    request.quoteSpeechSettings,
                    request.includeTitle,
                    request.clientRequestId,
                    request.pageAudioDiagnostics
                );

            if (requestId !== this.pageAudioRequestId) {
                return;
            }

            if (!job || !job.jobId) {
                throw new Error("Unable to start Book of Mind page audio.");
            }

            this.UpdatePageAudioProgress(
                job.completedSteps,
                job.totalSteps
            );

            if (job.status === "error") {
                throw new Error(
                    job.error ?? "Unable to generate Book of Mind page audio."
                );
            }

            this.pageAudioJobId = job.jobId;
            jobId = job.jobId;
            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-job-started",
                {
                    clientRequestId: request.clientRequestId,
                    requestId,
                    jobId,
                    status: job.status,
                }
            );
        }

        await this.WaitForPageAudioJobAsync(requestId, jobId, request.title);
    }

    private async WaitForPageAudioJobAsync(
        requestId: number,
        jobId: string,
        title: string
    ) {
        const deadline = Date.now() + pageAudioJobMaximumWaitMs;
        const pollingStartedAt = Date.now();
        let pollCount = 0;
        let previousStatus: string | undefined;
        let previousCompletedSteps: number | undefined;
        let previousTotalSteps: number | undefined;
        while (requestId === this.pageAudioRequestId) {
            pollCount++;
            const job: IBookOfMindPageSpeechJob =
                await MotwLogicServer.GetBookOfMindPageSpeechJobAsync(
                    jobId,
                    this.pageAudioClientRequestId,
                    pollCount === 1
                );

            if (requestId !== this.pageAudioRequestId) {
                return;
            }

            this.UpdatePageAudioProgress(
                job.completedSteps,
                job.totalSteps
            );

            if (
                pollCount === 1 ||
                job.status !== previousStatus ||
                job.completedSteps !== previousCompletedSteps ||
                job.totalSteps !== previousTotalSteps
            ) {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "page-audio-job-status",
                    {
                        clientRequestId: this.pageAudioClientRequestId,
                        requestId,
                        jobId,
                        status: job.status,
                        completedSteps: job.completedSteps,
                        totalSteps: job.totalSteps,
                        pollCount,
                        elapsedMs: Date.now() - pollingStartedAt,
                    },
                    job.status === "error" ? "error" : "info"
                );
                previousStatus = job.status;
                previousCompletedSteps = job.completedSteps;
                previousTotalSteps = job.totalSteps;
            }

            if (job.status === "ready") {
                const audioBlob =
                    await MotwLogicServer.GetBookOfMindPageSpeechAudioAsync(
                        jobId,
                        this.pageAudioClientRequestId
                    );
                await this.PlayPageAudioBlobAsync(
                    requestId,
                    title,
                    audioBlob
                );
                return;
            }

            if (job.status === "error") {
                throw new Error(
                    job.error ?? "Unable to generate Book of Mind page audio."
                );
            }

            if (Date.now() >= deadline) {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "page-audio-job-timeout",
                    {
                        clientRequestId: this.pageAudioClientRequestId,
                        requestId,
                        jobId,
                        pollCount,
                        elapsedMs: Date.now() - pollingStartedAt,
                    },
                    "error"
                );
                throw new Error(
                    "Book of Mind page audio is taking too long to prepare."
                );
            }

            await WaitAsync(pageAudioJobPollIntervalMs);
        }
    }

    private async PlayPageAudioBlobAsync(
        requestId: number,
        title: string,
        audioBlob: Blob
    ) {
        if (
            requestId !== this.pageAudioRequestId ||
            this.pageAudioState !== "preparing" ||
            this.pageAudioBlob
        ) {
            return;
        }

        await this.WaitForGoogleAudioUnlockAsync();

        if (requestId !== this.pageAudioRequestId) {
            return;
        }

        const audio = this.GetGoogleAudioElement();
        const audioUrl = URL.createObjectURL(audioBlob);
        this.pageAudioBlob = audioBlob;
        this.pageAudioUrl = audioUrl;
        audio.muted = false;
        audio.src = audioUrl;
        audio.onplay = () => {
            if (requestId === this.pageAudioRequestId) {
                this.SetPageAudioState("playing");
            }
        };
        audio.onpause = () => {
            if (requestId === this.pageAudioRequestId && !audio.ended) {
                this.SetPageAudioState("paused");
            }
        };
        audio.onended = () => {
            if (requestId !== this.pageAudioRequestId) {
                return;
            }

            this.ClearPageAudioMediaSession();
            this.SetPageAudioState("ready");
        };
        audio.onerror = () => {
            if (requestId !== this.pageAudioRequestId) {
                return;
            }

            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-play-error",
                {
                    clientRequestId: this.pageAudioClientRequestId,
                    requestId,
                    audio: GetAudioDiagnostics(audio),
                },
                "error"
            );
            this.SetPageAudioError(
                "The Book of Mind page audio could not be played."
            );
        };

        this.ConfigurePageAudioMediaSession(title, audio);
        LogClientDiagnostic(
            "book-of-mind-speech",
            "page-audio-play-call",
            {
                clientRequestId: this.pageAudioClientRequestId,
                requestId,
                audioByteLength: audioBlob.size,
                audioBlobType: audioBlob.type,
            }
        );
        try {
            await audio.play();
        } catch (error) {
            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-play-error",
                {
                    clientRequestId: this.pageAudioClientRequestId,
                    requestId,
                    audio: GetAudioDiagnostics(audio),
                    error: GetClientErrorDetails(error),
                },
                "error"
            );
            throw error;
        }
        LogClientDiagnostic(
            "book-of-mind-speech",
            "page-audio-play-resolved",
            {
                clientRequestId: this.pageAudioClientRequestId,
                requestId,
            }
        );
        if (requestId === this.pageAudioRequestId) {
            this.SetPageAudioState("playing");
            this.PlayPageAudioCompletionCue();
        }
    }

    private HandlePageAudioResume = () => {
        if (document.hidden) {
            return;
        }

        this.FlushPageAudioCuesAfterResume();

        if (
            this.pageAudioState !== "preparing" ||
            !this.pageAudioJobRequest ||
            this.pageAudioResumePromise
        ) {
            return;
        }

        const requestId = this.pageAudioRequestId;
        const request = this.pageAudioJobRequest;
        this.pageAudioResumePromise = this.StartOrResumePageAudioJobAsync(
            requestId,
            request
        )
            .catch((error: unknown) => {
                if (
                    requestId === this.pageAudioRequestId &&
                    this.pageAudioState === "preparing"
                ) {
                    this.SetPageAudioError(
                        error instanceof Error
                            ? error.message
                            : "Unable to resume Book of Mind page audio."
                    );
                }
            })
            .finally(() => {
                this.pageAudioResumePromise = undefined;
            });
    };

    private async WaitForGoogleAudioUnlockAsync() {
        const unlockPromise = this.googleAudioUnlockPromise;
        if (!unlockPromise) {
            return;
        }

        const unlockCompleted = await Promise.race([
            unlockPromise.then(() => true),
            WaitAsync(googleAudioUnlockMaximumWaitMs).then(() => false),
        ]);

        if (unlockCompleted || this.googleAudioUnlockPromise !== unlockPromise) {
            return;
        }

        this.googleAudioUnlockPromise = undefined;
        const audio = this.GetGoogleAudioElement();
        audio.pause();
        audio.muted = false;
        audio.removeAttribute("src");
        audio.load();
        LogClientDiagnostic(
            "book-of-mind-speech",
            "google-audio-unlock-timeout",
            { timeoutMs: googleAudioUnlockMaximumWaitMs },
            "warn"
        );
    }

    PausePageAudio() {
        if (this.pageAudioState !== "playing") {
            return;
        }

        this.GetGoogleAudioElement().pause();
        this.SetPageAudioState("paused");
        if ("mediaSession" in navigator) {
            navigator.mediaSession.playbackState = "paused";
        }
    }

    async ResumePageAudioAsync() {
        if (!this.pageAudioBlob) {
            return;
        }

        const audio = this.GetGoogleAudioElement();
        if (audio.ended) {
            audio.currentTime = 0;
        }
        this.ConfigurePageAudioMediaSession(this.pageAudioTitle ?? "Book of Mind", audio);
        await audio.play();
        this.SetPageAudioState("playing");
    }

    StopPageAudio(reason = "explicit") {
        if (this.pageAudioState !== "idle" || this.pageAudioClientRequestId) {
            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-stop",
                {
                    clientRequestId: this.pageAudioClientRequestId,
                    requestId: this.pageAudioRequestId,
                    state: this.pageAudioState,
                    reason,
                }
            );
        }
        this.pageAudioRequestId++;
        this.ClearPageAudioMediaSession();

        const audio = this.GetGoogleAudioElement();
        audio.onplay = null;
        audio.onpause = null;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.removeAttribute("src");
        audio.load();

        if (this.pageAudioUrl) {
            URL.revokeObjectURL(this.pageAudioUrl);
        }

        this.pageAudioUrl = undefined;
        this.pageAudioBlob = undefined;
        this.pageAudioKey = undefined;
        this.pageAudioTitle = undefined;
        this.pageAudioJobId = undefined;
        this.pageAudioJobRequest = undefined;
        this.pageAudioClientRequestId = undefined;
        this.pageAudioCompletedSteps = undefined;
        this.pageAudioTotalSteps = undefined;
        this.ResetPageAudioCueState();
        this.SetPageAudioState("idle");
    }

    StopNoSleep() {
        const noSleep = new NoSleep();
        noSleep.disable();
        console.log(`nosleep stopped`);
    }

    StopSpeech() {
        LogClientDiagnostic("book-of-mind-speech", "stop-request");
        this.pauseRequestId++;
        this.ClearPauseRetry();
        if (!this.preservePendingStartOnNextStop) {
            this.ClearPendingStart();
        }
        this.preservePendingStartOnNextStop = false;
        this.StopPlayback();

        SetBookOfMindState({
            isSpeechStarting: false,
            isSpeechActive: false,
            spokenQuoteIndex: undefined,
            spokenWordStartIndex: undefined,
            spokenWordEndIndex: undefined,
            itemsToSpeak: [],
        });
    }

    StopPlayback(stopPageAudio = true) {
        this.CancelGoogleSpeech();
        this.StopActiveWordHighlight();
        window.speechSynthesis.cancel();
        if (stopPageAudio) {
            this.StopPageAudio("stop-playback");
        }
        Ambience.UnDuck();
        Music.UnDuck();
    }

    ScheduleStartAfterPageTurn(milliseconds: number) {
        this.ClearPendingStart();
        this.preservePendingStartOnNextStop = true;
        this.pendingStartTimeoutId = window.setTimeout(() => {
            this.pendingStartTimeoutId = undefined;
            this.preservePendingStartOnNextStop = false;
            void this.StartAsync();
        }, milliseconds);
    }

    Pause() {
        const hasGooglePlayback =
            this.activeGoogleSpeechRequestId !== undefined ||
            this.activeGoogleAudio !== undefined;
        LogClientDiagnostic("book-of-mind-speech", "pause-request", {
            engine: hasGooglePlayback ? "google" : "browser",
            browserSpeaking: window.speechSynthesis.speaking,
            browserPaused: window.speechSynthesis.paused,
        });
        if (hasGooglePlayback) {
            this.isGoogleSpeechPaused = true;
            this.activeGoogleAudio?.pause();
            SetBookOfMindState({ isSpeechActive: false });
            return;
        }

        const pauseRequestId = ++this.pauseRequestId;
        this.ClearPauseRetry();
        this.TryPause(pauseRequestId, 0);
        SetBookOfMindState({ isSpeechActive: false });
    }

    Resume() {
        const hasGooglePlayback =
            this.activeGoogleSpeechRequestId !== undefined ||
            this.activeGoogleAudio !== undefined;
        LogClientDiagnostic("book-of-mind-speech", "resume-request", {
            engine: hasGooglePlayback ? "google" : "browser",
            browserSpeaking: window.speechSynthesis.speaking,
            browserPaused: window.speechSynthesis.paused,
        });
        if (hasGooglePlayback) {
            this.isGoogleSpeechPaused = false;
            const audio = this.activeGoogleAudio;
            if (audio) {
                void audio
                    .play()
                    .then(() => {
                        if (this.isGoogleSpeechPaused) {
                            audio.pause();
                        }
                    })
                    .catch((error: unknown) => {
                        LogClientDiagnostic(
                            "book-of-mind-speech",
                            "google-resume-error",
                            { error: GetClientErrorDetails(error) },
                            "error"
                        );
                        console.error(
                            "Book of Mind Google speech could not resume",
                            error
                        );
                    });
            }
            SetBookOfMindState({ isSpeechActive: true });
            return;
        }

        this.pauseRequestId++;
        this.ClearPauseRetry();
        window.speechSynthesis.resume();
        SetBookOfMindState({ isSpeechActive: true });
    }

    async PlayQuoteWithOffsetAsync(offset: number) {
        LogClientDiagnostic("book-of-mind-speech", "quote-navigation", {
            offset,
        });
        const state = ReadState<IBookOfMindState>(ESharedFunction.BookOfMind);

        this.pauseRequestId++;
        this.ClearPauseRetry();
        this.StopActiveWordHighlight();
        this.CancelGoogleSpeech();
        window.speechSynthesis.cancel();

        const startSpeechIndex = state!.spokenQuoteIndex! + offset;

        SetBookOfMindState({
            itemsToSpeak: [],
            spokenQuoteIndex: startSpeechIndex,
            spokenWordStartIndex: undefined,
            spokenWordEndIndex: undefined,
            isSpeechActive: true,
        });

        await WaitAsync(1000);

        const itemsToSpeak = GetSpeechItems();

        SetBookOfMindState({
            itemsToSpeak,
            spokenQuoteIndex: startSpeechIndex,
            spokenWordStartIndex: undefined,
            spokenWordEndIndex: undefined,
            isSpeechActive: true,
        });
    }

    private SpeakTextAsync(
        text: string,
        preferredVoiceName?: string,
        shouldHighlightWords: boolean = false,
        quoteIndex?: number,
        onPlaybackStarted?: () => void
    ): Promise<string> {
        const synth = window.speechSynthesis;
        const voices = synth.getVoices();

        // iOS may expose no voices even though its default voice can speak.
        // Keep speak() on the initiating call path instead of waiting for
        // voiceschanged, which can lose iOS's user-gesture permission.
        return this.SpeakTextWithVoicesAsync(
            text,
            voices,
            preferredVoiceName,
            shouldHighlightWords,
            true,
            quoteIndex,
            onPlaybackStarted
        );
    }

    private SpeakTextWithVoicesAsync(
        text: string,
        voices: SpeechSynthesisVoice[],
        preferredVoiceName?: string,
        shouldHighlightWords: boolean = false,
        allowGoogleSpeech: boolean = true,
        quoteIndex?: number,
        onPlaybackStarted?: () => void
    ): Promise<string> {
        const voiceName = preferredVoiceName ?? GetBookOfMindSavedVoiceName();

        if (allowGoogleSpeech && IsGoogleBookOfMindVoice(voiceName)) {
            return this.SpeakGoogleTextAsync(
                text,
                shouldHighlightWords,
                GetGoogleBookOfMindVoiceNameForQuote(voiceName, quoteIndex),
                quoteIndex,
                onPlaybackStarted
            );
        }

        const selectedVoice = ResolveBookOfMindVoice(
            voices,
            voiceName
        );

        if (!selectedVoice) {
            return this.SpeakTextWithVoiceAsync(
                text,
                undefined,
                shouldHighlightWords,
                quoteIndex
            );
        }

        return this.SpeakTextWithVoiceAsync(
            text,
            selectedVoice,
            shouldHighlightWords,
            quoteIndex
        );
    }

    private async SpeakGoogleTextAsync(
        text: string,
        shouldHighlightWords: boolean,
        voiceName: string,
        quoteIndex?: number,
        onPlaybackStarted?: () => void
    ): Promise<string> {
        const requestId = ++this.googleSpeechRequestId;
        let failureStage = "request";
        let lastAudioDiagnostics:
            | ReturnType<typeof GetAudioDiagnostics>
            | undefined;
        this.activeGoogleSpeechRequestId = requestId;
        this.isGoogleSpeechPaused = false;
        this.StopActiveGoogleAudio();
        this.PrepareGoogleAudioForUserGesture();
        this.StopActiveWordHighlight();
        Ambience.Duck();
        Music.Duck();
        const speechDetails = {
            requestId,
            voiceName,
            textLength: text.length,
            shouldHighlightWords,
            ...(quoteIndex === undefined ? {} : { quoteIndex }),
        };
        LogClientDiagnostic(
            "book-of-mind-speech",
            "google-request",
            speechDetails
        );

        try {
            const audioBlob = await this.GetGoogleSpeechAudioAsync(
                text,
                voiceName,
                GetGoogleBookOfMindSpeechSettings(voiceName)
            );

            LogClientDiagnostic(
                "book-of-mind-speech",
                "google-fetch-complete",
                {
                    ...speechDetails,
                    byteLength: audioBlob.size,
                    blobType: audioBlob.type,
                }
            );

            failureStage = "audio-create";

            if (this.activeGoogleSpeechRequestId !== requestId) {
                return `cancelled speaking '${text}'`;
            }

            if (this.googleAudioUnlockPromise) {
                await this.googleAudioUnlockPromise;
            }

            const audioUrl = URL.createObjectURL(audioBlob);
            const googleAudio = this.GetGoogleAudioElement();
            googleAudio.muted = false;
            googleAudio.src = audioUrl;
            this.activeGoogleAudio = googleAudio;
            this.activeGoogleAudioUrl = audioUrl;
            const logAudioEvent = (event: string) => {
                lastAudioDiagnostics = GetAudioDiagnostics(googleAudio);
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    event,
                    { ...speechDetails, audio: lastAudioDiagnostics },
                    event === "google-audio-error" ? "error" : "info"
                );
            };

            logAudioEvent("google-audio-created");
            googleAudio.onloadedmetadata = () =>
                logAudioEvent("google-audio-loadedmetadata");
            googleAudio.oncanplay = () =>
                logAudioEvent("google-audio-canplay");
            googleAudio.onplaying = () =>
                logAudioEvent("google-audio-playing");
            googleAudio.onwaiting = () =>
                logAudioEvent("google-audio-waiting");
            googleAudio.onstalled = () =>
                logAudioEvent("google-audio-stalled");
            googleAudio.onpause = () =>
                logAudioEvent("google-audio-paused");

            const activeWordHighlight = shouldHighlightWords
                ? CreateAudioWordHighlight(text, googleAudio)
                : undefined;

            if (activeWordHighlight) {
                this.StopActiveWordHighlight();
                this.activeWordHighlight = activeWordHighlight;
                activeWordHighlight.Start();
            }

            const waitForAudio = new Promise<void>((resolve, reject) => {
                const finish = () => {
                    googleAudio.onended = null;
                    googleAudio.onerror = null;
                    if (this.finishActiveGoogleAudio === finish) {
                        this.finishActiveGoogleAudio = undefined;
                    }
                    resolve();
                };

                googleAudio.onended = () => {
                    logAudioEvent("google-audio-ended");
                    finish();
                };
                googleAudio.onerror = () => {
                    googleAudio.onended = null;
                    googleAudio.onerror = null;
                    if (this.finishActiveGoogleAudio === finish) {
                        this.finishActiveGoogleAudio = undefined;
                    }
                    LogClientDiagnostic(
                        "book-of-mind-speech",
                        "google-audio-error",
                        {
                            ...speechDetails,
                            audio: GetAudioDiagnostics(googleAudio),
                        },
                        "error"
                    );
                    reject(new Error("Google TTS audio playback failed."));
                };
                this.finishActiveGoogleAudio = finish;
            });

            if (!this.isGoogleSpeechPaused) {
                failureStage = "audio-play";
                logAudioEvent("google-audio-play-call");
                await googleAudio.play();
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "google-play-resolved",
                    {
                        ...speechDetails,
                        audio: GetAudioDiagnostics(googleAudio),
                    }
                );
                onPlaybackStarted?.();
                if (
                    this.isGoogleSpeechPaused ||
                    this.activeGoogleSpeechRequestId !== requestId
                ) {
                    googleAudio.pause();
                }
            }

            failureStage = "audio-wait";
            await waitForAudio;

            const wasCancelled =
                this.activeGoogleSpeechRequestId !== requestId;
            this.StopActiveWordHighlight();
            this.StopActiveGoogleAudio();
            if (this.activeGoogleSpeechRequestId === requestId) {
                this.activeGoogleSpeechRequestId = undefined;
            }
            Ambience.UnDuck();
            Music.UnDuck();

            LogClientDiagnostic(
                "book-of-mind-speech",
                wasCancelled ? "google-cancelled" : "google-complete",
                speechDetails
            );

            return wasCancelled
                ? `cancelled speaking '${text}'`
                : `finished speaking '${text}'`;
        } catch (error) {
            const wasCancelled =
                this.activeGoogleSpeechRequestId !== requestId;
            this.StopActiveWordHighlight();
            this.StopActiveGoogleAudio();
            if (this.activeGoogleSpeechRequestId === requestId) {
                this.activeGoogleSpeechRequestId = undefined;
            }
            Ambience.UnDuck();
            Music.UnDuck();

            if (wasCancelled) {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "google-cancelled",
                    speechDetails,
                    "debug"
                );
                return `cancelled speaking '${text}'`;
            }

            LogClientDiagnostic(
                "book-of-mind-speech",
                "google-error",
                {
                    ...speechDetails,
                    failureStage,
                    ...(lastAudioDiagnostics
                        ? { audio: lastAudioDiagnostics }
                        : {}),
                    error: GetClientErrorDetails(error),
                },
                "error"
            );
            console.error(
                "Book of Mind Google speech failed; falling back to browser speech",
                error
            );

            LogClientDiagnostic(
                "book-of-mind-speech",
                "browser-fallback",
                { ...speechDetails, failureStage },
                "warn"
            );
            return this.SpeakBrowserTextAsync(
                text,
                shouldHighlightWords,
                quoteIndex
            );
        }
    }

    private SpeakBrowserTextAsync(
        text: string,
        shouldHighlightWords: boolean,
        quoteIndex?: number
    ): Promise<string> {
        return this.SpeakTextWithVoicesAsync(
            text,
            window.speechSynthesis.getVoices(),
            undefined,
            shouldHighlightWords,
            false,
            quoteIndex
        );
    }

    private SpeakTextWithVoiceAsync(
        text: string,
        selectedVoice: SpeechSynthesisVoice | undefined,
        shouldHighlightWords: boolean = false,
        quoteIndex?: number
    ): Promise<string> {
        return new Promise((resolve: (value: string) => void) => {
            const synth = window.speechSynthesis;
            const utterance = new SpeechSynthesisUtterance(text);
            let hasFinished = false;
            const speechDetails = {
                voiceName: selectedVoice?.name,
                textLength: text.length,
                shouldHighlightWords,
                ...(quoteIndex === undefined ? {} : { quoteIndex }),
            };
            LogClientDiagnostic(
                "book-of-mind-speech",
                "browser-request",
                speechDetails
            );
            windowUtterances.push(utterance); // To fix an iOS garbage-collection bug
            if (selectedVoice) {
                utterance.voice = selectedVoice;
                utterance.lang = selectedVoice.lang;
            }

            const activeWordHighlight = shouldHighlightWords
                ? CreateActiveWordHighlight(text, utterance.rate)
                : undefined;

            if (shouldHighlightWords) {
                this.StopActiveWordHighlight();
                this.activeWordHighlight = activeWordHighlight;
                utterance.onboundary = activeWordHighlight!.HandleBoundary;
            }

            const finishSpeaking = () => {
                if (hasFinished) return;
                hasFinished = true;
                Ambience.UnDuck();
                Music.UnDuck();

                activeWordHighlight?.Stop();
                if (this.activeWordHighlight === activeWordHighlight) {
                    this.activeWordHighlight = undefined;
                }

                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "browser-complete",
                    speechDetails
                );

                resolve(`finished speaking '${text}'`);
            };
            utterance.onstart = () => {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "browser-started",
                    speechDetails
                );
                activeWordHighlight?.Start();
            };
            utterance.onend = finishSpeaking;
            utterance.onerror = (error) => {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "browser-error",
                    { ...speechDetails, error: GetClientErrorDetails(error) },
                    "error"
                );
                finishSpeaking();
            };

            Ambience.Duck();
            Music.Duck();
            synth.speak(utterance);
            activeWordHighlight?.Start();
        });
    }

    private StopActiveWordHighlight() {
        this.activeWordHighlight?.Stop();
        this.activeWordHighlight = undefined;
    }

    private GetGoogleAudioElement() {
        if (!this.googleAudioElement) {
            this.googleAudioElement = new Audio();
            this.googleAudioElement.preload = "auto";
        }

        return this.googleAudioElement;
    }

    private PrepareGoogleAudioForUserGesture() {
        if (this.googleAudioUnlockPromise) {
            return;
        }

        const audio = this.GetGoogleAudioElement();
        audio.muted = true;
        audio.src = googleAudioUnlockSource;
        LogClientDiagnostic(
            "book-of-mind-speech",
            "google-audio-unlock-request"
        );

        try {
            this.googleAudioUnlockPromise = Promise.resolve(audio.play())
                .then(() => {
                    audio.pause();
                    audio.currentTime = 0;
                    audio.muted = false;
                    audio.removeAttribute("src");
                    audio.load();
                    LogClientDiagnostic(
                        "book-of-mind-speech",
                        "google-audio-unlock-complete"
                    );
                })
                .catch((error: unknown) => {
                    this.googleAudioUnlockPromise = undefined;
                    audio.pause();
                    audio.muted = false;
                    audio.removeAttribute("src");
                    audio.load();
                    LogClientDiagnostic(
                        "book-of-mind-speech",
                        "google-audio-unlock-error",
                        { error: GetClientErrorDetails(error) },
                        "warn"
                    );
                });
        } catch (error) {
            this.googleAudioUnlockPromise = undefined;
            audio.muted = false;
            audio.removeAttribute("src");
            audio.load();
            LogClientDiagnostic(
                "book-of-mind-speech",
                "google-audio-unlock-error",
                { error: GetClientErrorDetails(error) },
                "warn"
            );
        }
    }

    private CancelGoogleSpeech() {
        this.googleSpeechRequestId++;
        this.InvalidateGoogleSpeechPrefetch();
        this.activeGoogleSpeechRequestId = undefined;
        this.isGoogleSpeechPaused = false;
        this.StopActiveGoogleAudio();
    }

    private StopActiveGoogleAudio() {
        this.finishActiveGoogleAudio?.();
        this.finishActiveGoogleAudio = undefined;

        const audio = this.activeGoogleAudio;
        const audioUrl = this.activeGoogleAudioUrl;
        this.activeGoogleAudio = undefined;
        this.activeGoogleAudioUrl = undefined;

        if (audio) {
            audio.onended = null;
            audio.onerror = null;
            audio.onloadedmetadata = null;
            audio.oncanplay = null;
            audio.onplaying = null;
            audio.onwaiting = null;
            audio.onstalled = null;
            audio.onpause = null;
            audio.pause();
            audio.removeAttribute("src");
            audio.load();
        }

        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
        }
    }

    InvalidateGoogleSpeechPrefetch() {
        this.googleSpeechSequenceId++;
        this.prefetchedGoogleSpeech = undefined;
    }

    private async GetGoogleSpeechAudioAsync(
        text: string,
        voiceName: string,
        speechSettings: ReturnType<
            typeof GetGoogleBookOfMindSpeechSettings
        >
    ) {
        const prefetchedSpeech = this.prefetchedGoogleSpeech;
        if (
            prefetchedSpeech &&
            prefetchedSpeech.sequenceId === this.googleSpeechSequenceId &&
            prefetchedSpeech.text === text &&
            prefetchedSpeech.voiceName === voiceName
        ) {
            this.prefetchedGoogleSpeech = undefined;
            try {
                return await prefetchedSpeech.audioPromise;
            } catch {
                // A failed best-effort prefetch must not stop the speech chain.
            }
        }

        return await MotwLogicServer.SynthesizeBookOfMindSpeechAsync(
            text,
            voiceName,
            speechSettings
        );
    }

    PrefetchNextGoogleSpeech(text: string, quoteIndex?: number) {
        const savedVoiceName = GetBookOfMindSavedVoiceName();
        if (!IsGoogleBookOfMindVoice(savedVoiceName)) {
            return;
        }

        const voiceName = GetGoogleBookOfMindVoiceNameForQuote(
            savedVoiceName,
            quoteIndex
        );
        const existingPrefetch = this.prefetchedGoogleSpeech;
        if (
            existingPrefetch &&
            existingPrefetch.sequenceId === this.googleSpeechSequenceId &&
            existingPrefetch.text === text &&
            existingPrefetch.voiceName === voiceName
        ) {
            return;
        }

        const sequenceId = this.googleSpeechSequenceId;
        const audioPromise = MotwLogicServer.SynthesizeBookOfMindSpeechAsync(
            text,
            voiceName,
            GetGoogleBookOfMindSpeechSettings(voiceName)
        );
        const prefetchedSpeech: IPrefetchedGoogleSpeech = {
            sequenceId,
            text,
            voiceName,
            audioPromise,
        };
        this.prefetchedGoogleSpeech = prefetchedSpeech;

        void audioPromise.catch((error) => {
            if (this.prefetchedGoogleSpeech === prefetchedSpeech) {
                this.prefetchedGoogleSpeech = undefined;
            }

            LogClientDiagnostic(
                "book-of-mind-speech",
                "google-prefetch-error",
                {
                    sequenceId,
                    voiceName,
                    textLength: text.length,
                    error: GetClientErrorDetails(error),
                },
                "warn"
            );
        });
    }

    private SetPageAudioState(state: BookOfMindPageAudioState) {
        this.pageAudioState = state;
        this.pageAudioError = undefined;
        LogClientDiagnostic("book-of-mind-speech", "page-audio-state", {
            clientRequestId: this.pageAudioClientRequestId,
            state,
        });
        this.NotifyPageAudioListeners();
    }

    private UpdatePageAudioProgress(
        completedSteps?: number,
        totalSteps?: number
    ) {
        const hasProgress =
            typeof completedSteps === "number" &&
            typeof totalSteps === "number" &&
            Number.isInteger(completedSteps) &&
            Number.isInteger(totalSteps) &&
            completedSteps >= 0 &&
            totalSteps > 0;
        const nextTotalSteps = hasProgress ? totalSteps : undefined;
        const nextCompletedSteps = hasProgress
            ? Math.min(completedSteps, totalSteps)
            : undefined;

        if (
            this.pageAudioCompletedSteps === nextCompletedSteps &&
            this.pageAudioTotalSteps === nextTotalSteps
        ) {
            return;
        }

        this.pageAudioCompletedSteps = nextCompletedSteps;
        this.pageAudioTotalSteps = nextTotalSteps;
        if (nextCompletedSteps !== undefined) {
            this.QueuePageAudioProgressCues(nextCompletedSteps);
        }
        this.NotifyPageAudioListeners();
    }

    private SetPageAudioError(error: string) {
        this.pageAudioState = "idle";
        this.pageAudioError = error;
        this.pageAudioCompletedSteps = undefined;
        this.pageAudioTotalSteps = undefined;
        this.ResetPageAudioCueState();
        this.ClearPageAudioMediaSession();
        LogClientDiagnostic(
            "book-of-mind-speech",
            "page-audio-state",
            {
                clientRequestId: this.pageAudioClientRequestId,
                state: "idle",
                error,
            },
            "error"
        );
        this.NotifyPageAudioListeners();
    }

    private ResetPageAudioCueState() {
        if (this.pageAudioProgressCueTimerId !== undefined) {
            window.clearTimeout(this.pageAudioProgressCueTimerId);
            this.pageAudioProgressCueTimerId = undefined;
        }

        if (this.pageAudioCompletionCueTimeoutId !== undefined) {
            window.clearTimeout(this.pageAudioCompletionCueTimeoutId);
            this.pageAudioCompletionCueTimeoutId = undefined;
        }

        this.pageAudioProgressCueQueue = [];
        this.pageAudioProgressCuePendingAfterResume = false;
        this.pageAudioProgressCueLastStep = 0;
        this.pageAudioCompletionCuePending = false;
        this.pageAudioCompletionCuePlayed = false;
    }

    private PlayPageAudioStartCue() {
        Sounds.electronic_tone_soft.Play({
            allowConcurrent: true,
            allowMobile: true,
            volume: 0.05,
        });
    }

    private QueuePageAudioProgressCues(completedSteps: number) {
        const newlyCompletedSteps =
            completedSteps - this.pageAudioProgressCueLastStep;
        if (newlyCompletedSteps <= 0) {
            return;
        }

        this.pageAudioProgressCueLastStep = completedSteps;
        if (document.hidden) {
            this.pageAudioProgressCueQueue = [];
            this.pageAudioProgressCuePendingAfterResume = true;
            return;
        }

        const totalSteps = this.pageAudioTotalSteps ?? completedSteps;
        for (let stepOffset = 1; stepOffset <= newlyCompletedSteps; stepOffset++) {
            this.pageAudioProgressCueQueue.push(
                this.GetPageAudioProgressCueRate(
                    completedSteps - newlyCompletedSteps + stepOffset,
                    totalSteps
                )
            );
        }
        this.DrainPageAudioProgressCueQueue();
    }

    private DrainPageAudioProgressCueQueue() {
        if (
            document.hidden ||
            this.pageAudioProgressCueQueue.length === 0 ||
            this.pageAudioProgressCueTimerId !== undefined
        ) {
            return;
        }

        this.PlayPageAudioProgressCue(this.pageAudioProgressCueQueue.shift()!);
        this.pageAudioProgressCueTimerId = window.setTimeout(() => {
            this.pageAudioProgressCueTimerId = undefined;
            this.DrainPageAudioProgressCueQueue();
        }, 220);
    }

    private FlushPageAudioCuesAfterResume() {
        const hasPendingProgressCue =
            this.pageAudioProgressCuePendingAfterResume ||
            this.pageAudioProgressCueQueue.length > 0;

        if (hasPendingProgressCue) {
            if (this.pageAudioProgressCueTimerId !== undefined) {
                window.clearTimeout(this.pageAudioProgressCueTimerId);
                this.pageAudioProgressCueTimerId = undefined;
            }

            this.pageAudioProgressCueQueue = [];
            this.pageAudioProgressCuePendingAfterResume = false;
            this.PlayPageAudioProgressCue(
                this.GetPageAudioProgressCueRate(
                    this.pageAudioCompletedSteps ??
                        this.pageAudioProgressCueLastStep,
                    this.pageAudioTotalSteps ??
                        this.pageAudioProgressCueLastStep
                )
            );
        }

        if (this.pageAudioCompletionCuePending) {
            if (this.pageAudioCompletionCueTimeoutId !== undefined) {
                window.clearTimeout(this.pageAudioCompletionCueTimeoutId);
            }

            const requestId = this.pageAudioRequestId;
            this.pageAudioCompletionCueTimeoutId = window.setTimeout(
                () => {
                    this.pageAudioCompletionCueTimeoutId = undefined;
                    if (requestId !== this.pageAudioRequestId) {
                        return;
                    }

                    this.PlayPageAudioCompletionCue();
                },
                hasPendingProgressCue ? 220 : 0
            );
        }
    }

    private GetPageAudioProgressCueRate(
        completedSteps: number,
        totalSteps: number
    ) {
        const progressRatio =
            totalSteps > 0
                ? Math.min(1, Math.max(0, completedSteps / totalSteps))
                : 0;

        return 1.8 + progressRatio * 0.8;
    }

    private PlayPageAudioProgressCue(rate: number) {
        Sounds.electronic_tone_low_soft.Play({
            allowConcurrent: true,
            allowMobile: true,
            volume: 0.05,
            rate,
        });
    }

    private PlayPageAudioCompletionCue() {
        if (this.pageAudioCompletionCuePlayed) {
            return;
        }

        if (document.hidden) {
            this.pageAudioCompletionCuePending = true;
            return;
        }

        this.pageAudioCompletionCuePending = false;
        this.pageAudioCompletionCuePlayed = true;
        Sounds.electronic_tones_melodic_positive2.Play({
            allowConcurrent: true,
            allowMobile: true,
            volume: 0.05,
        });
    }

    private NotifyPageAudioListeners() {
        const snapshot = this.GetPageAudioSnapshot();
        this.pageAudioListeners.forEach((listener) => listener(snapshot));
    }

    private ConfigurePageAudioMediaSession(
        title: string,
        audio: HTMLAudioElement
    ) {
        if (!("mediaSession" in navigator) || typeof MediaMetadata === "undefined") {
            return;
        }

        navigator.mediaSession.metadata = new MediaMetadata({
            title,
            artist: "Book of Mind",
            album: "The Final Act",
        });
        navigator.mediaSession.playbackState = "playing";

        this.SetMediaSessionAction("play", () => {
            void this.ResumePageAudioAsync();
        });
        this.SetMediaSessionAction("pause", () => this.PausePageAudio());
        this.SetMediaSessionAction("seekbackward", () => {
            audio.currentTime = Math.max(0, audio.currentTime - 15);
        });
        this.SetMediaSessionAction("seekforward", () => {
            audio.currentTime = Math.min(
                audio.duration,
                audio.currentTime + 15
            );
        });
        this.SetMediaSessionAction("stop", () =>
            this.StopPageAudio("media-session-stop")
        );
    }

    private SetMediaSessionAction(
        action: MediaSessionAction,
        handler: MediaSessionActionHandler
    ) {
        try {
            navigator.mediaSession.setActionHandler(action, handler);
        } catch {
            // Individual actions are not supported consistently across browsers.
        }
    }

    private ClearPageAudioMediaSession() {
        if (!("mediaSession" in navigator)) {
            return;
        }

        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = "none";
        ["play", "pause", "seekbackward", "seekforward", "stop"].forEach(
            (action) => {
                try {
                    navigator.mediaSession.setActionHandler(
                        action as MediaSessionAction,
                        null
                    );
                } catch {
                    // Individual actions are not supported consistently across browsers.
                }
            }
        );
    }

    private ClearPendingStart() {
        if (this.pendingStartTimeoutId === undefined) {
            return;
        }

        window.clearTimeout(this.pendingStartTimeoutId);
        this.pendingStartTimeoutId = undefined;
    }

    private TryPause(pauseRequestId: number, attempt: number) {
        const synth = window.speechSynthesis;
        synth.pause();

        if (
            pauseRequestId !== this.pauseRequestId ||
            synth.paused ||
            attempt >= 10 ||
            (!synth.speaking && !synth.pending)
        ) {
            return;
        }

        this.pauseRetryTimeoutId = window.setTimeout(() => {
            this.pauseRetryTimeoutId = undefined;
            this.TryPause(pauseRequestId, attempt + 1);
        }, 50);
    }

    private ClearPauseRetry() {
        if (this.pauseRetryTimeoutId === undefined) {
            return;
        }

        window.clearTimeout(this.pauseRetryTimeoutId);
        this.pauseRetryTimeoutId = undefined;
    }

    private async PlayRootNodeName() {
        const state = ReadState<IBookOfMindState>(ESharedFunction.BookOfMind)!;

        if (state.rootNodeName === "Related Quotes") {
            return false;
        }

        const textToPlay =
            state.rootNodeName === fromOthersNodeName
                ? `The Book of Mind`
                : state.rootNodeName;

        // required to get speech to work on iOS
        await this.SpeakSingleTextAsync(textToPlay);
        return true;
    }
}

interface ITextRange {
    startIndex: number;
    endIndex: number;
}

interface IActiveWordHighlight {
    Start: () => void;
    HandleBoundary: (event: SpeechSynthesisEvent) => void;
    Stop: () => void;
}

function CreateActiveWordHighlight(
    text: string,
    speechRate: number
): IActiveWordHighlight {
    const wordRanges = GetWordRanges(text);
    let fallbackTimeout: number | undefined;
    let hasNativeWordBoundaries = false;
    let hasStarted = false;
    let isStopped = false;

    const ClearFallbackTimeout = () => {
        if (fallbackTimeout !== undefined) {
            window.clearTimeout(fallbackTimeout);
            fallbackTimeout = undefined;
        }
    };

    const HighlightWord = (wordRange: ITextRange) => {
        SetBookOfMindState({
            spokenWordStartIndex: wordRange.startIndex,
            spokenWordEndIndex: wordRange.endIndex,
        });
    };

    const ScheduleFallbackWord = (wordIndex: number, delay: number) => {
        if (
            isStopped ||
            hasNativeWordBoundaries ||
            wordIndex >= wordRanges.length
        ) {
            return;
        }

        fallbackTimeout = window.setTimeout(() => {
            if (window.speechSynthesis.paused) {
                ScheduleFallbackWord(wordIndex, 100);
                return;
            }

            const wordRange = wordRanges[wordIndex];
            HighlightWord(wordRange);
            ScheduleFallbackWord(
                wordIndex + 1,
                GetEstimatedWordDuration(text, wordRange, speechRate)
            );
        }, delay);
    };

    return {
        Start: () => {
            if (isStopped || hasStarted || wordRanges.length === 0) {
                return;
            }

            hasStarted = true;
            HighlightWord(wordRanges[0]);
            ScheduleFallbackWord(
                1,
                GetEstimatedWordDuration(text, wordRanges[0], speechRate)
            );
        },
        HandleBoundary: (event: SpeechSynthesisEvent) => {
            if (isStopped || event.name.toLowerCase() === `sentence`) {
                return;
            }

            const wordRange = GetWordRangeAtIndex(
                wordRanges,
                event.charIndex
            );
            if (!wordRange) {
                return;
            }

            hasNativeWordBoundaries = true;
            ClearFallbackTimeout();
            HighlightWord(wordRange);
        },
        Stop: () => {
            isStopped = true;
            ClearFallbackTimeout();
            SetBookOfMindState({
                spokenWordStartIndex: undefined,
                spokenWordEndIndex: undefined,
            });
        },
    };
}

function CreateAudioWordHighlight(
    text: string,
    audio: HTMLAudioElement
): IActiveWordHighlight {
    const wordRanges = GetWordRanges(text);
    const wordWeights = wordRanges.map((wordRange) =>
        GetEstimatedWordDuration(text, wordRange, 1)
    );
    const totalWeight = wordWeights.reduce(
        (total, wordWeight) => total + wordWeight,
        0
    );
    let isStopped = false;

    const HighlightCurrentWord = () => {
        if (isStopped || wordRanges.length === 0) {
            return;
        }

        const duration = audio.duration;
        if (!Number.isFinite(duration) || duration <= 0 || totalWeight <= 0) {
            return;
        }

        const targetWeight = Math.min(
            Math.max(audio.currentTime / duration, 0),
            0.999999
        ) * totalWeight;

        let accumulatedWeight = 0;
        for (let index = 0; index < wordRanges.length; index++) {
            accumulatedWeight += wordWeights[index];
            if (targetWeight < accumulatedWeight) {
                const wordRange = wordRanges[index];
                SetBookOfMindState({
                    spokenWordStartIndex: wordRange.startIndex,
                    spokenWordEndIndex: wordRange.endIndex,
                });
                return;
            }
        }

        const lastWordRange = wordRanges[wordRanges.length - 1];
        SetBookOfMindState({
            spokenWordStartIndex: lastWordRange.startIndex,
            spokenWordEndIndex: lastWordRange.endIndex,
        });
    };

    return {
        Start: () => {
            if (isStopped || wordRanges.length === 0) {
                return;
            }

            const firstWordRange = wordRanges[0];
            SetBookOfMindState({
                spokenWordStartIndex: firstWordRange.startIndex,
                spokenWordEndIndex: firstWordRange.endIndex,
            });
            audio.addEventListener("loadedmetadata", HighlightCurrentWord);
            audio.addEventListener("timeupdate", HighlightCurrentWord);
            HighlightCurrentWord();
        },
        HandleBoundary: () => undefined,
        Stop: () => {
            isStopped = true;
            audio.removeEventListener("loadedmetadata", HighlightCurrentWord);
            audio.removeEventListener("timeupdate", HighlightCurrentWord);
            SetBookOfMindState({
                spokenWordStartIndex: undefined,
                spokenWordEndIndex: undefined,
            });
        },
    };
}

function GetWordRanges(text: string): ITextRange[] {
    const wordRanges: ITextRange[] = [];
    const wordPattern = /\S+/g;
    let match: RegExpExecArray | null;

    while ((match = wordPattern.exec(text)) !== null) {
        wordRanges.push({
            startIndex: match.index,
            endIndex: match.index + match[0].length,
        });
    }

    return wordRanges;
}

function GetWordRangeAtIndex(
    wordRanges: ITextRange[],
    charIndex: number
) {
    return (
        wordRanges.find(
            (wordRange) =>
                charIndex >= wordRange.startIndex &&
                charIndex < wordRange.endIndex
        ) ?? wordRanges.find((wordRange) => wordRange.startIndex >= charIndex)
    );
}

function GetEstimatedWordDuration(
    text: string,
    wordRange: ITextRange,
    speechRate: number
) {
    const word = text.slice(wordRange.startIndex, wordRange.endIndex);
    const punctuationPause = /[.!?]["')\]]?$/.test(word)
        ? 220
        : /[,;:]["')\]]?$/.test(word)
          ? 100
          : 0;
    const baseDuration = Math.max(180, Math.min(word.length * 55, 500));
    const normalizedSpeechRate = Math.max(speechRate, 0.1);

    return (baseDuration + punctuationPause) / normalizedSpeechRate;
}

function CreatePageAudioClientRequestId() {
    const randomPart = Math.random().toString(36).slice(2, 10);
    return `page-audio-${Date.now().toString(36)}-${randomPart}`;
}

export const bookOfMindSpeech = new BookOfMindSpeech();
